import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import React, { useEffect, useMemo, useState } from "react";
import { setTourStep, startTour, stopTour } from "../actions/guidedTour";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { HIPHEN_GREEN } from "../constants";
import { buildTourSteps } from "./interfaceGuidedTour";
import { hasAnalyticsRole } from "../users/rolesUtil";
import { refreshUser } from "../actions/user";
import { requestSetCompletedTour } from "../services/backendRequests";
import { selectSelectedTrialContractInfo } from "../selectors/resultMap";

// Which page of the tour the given path is, if any: the World Map, or the
// breeder report. Anything else pauses the tour instead of pointing at nothing.
//
// The map is matched with or without a site id: WorldMapLayout writes the id
// into the address bar with window.history.replaceState, behind the router's
// back, so a site already loaded elsewhere (Analytics) and reached through the
// navbar's plain /map leaves the router on /map.
const pageOf = (pathname) => {
  if (/^\/map(\/|$)/.test(pathname)) return "map";
  if (pathname === "/analytics/breeder") return "analytics";
  return null;
};

// Mounted once in Layout, which wraps the router Switch without a path and so
// never unmounts: the same tour, with the same step counter, survives the jump
// from the World Map to Analytics. It also starts the tour itself, the first
// time the user opens a site on the map.
export const GuidedTourController = () => {
  const dispatch = useDispatch();
  const { active, stepIndex, startedOnce } = useSelector(
    (store) => store.guidedTour
  );
  const user = useSelector((store) => store.user);
  const tourDone = user.self?.completed_guided_tour;
  const history = useHistory();
  const { pathname } = useLocation();
  const [trial, refreshing, traitsListForMap] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.refreshing,
    resultMap.traitsListForMap,
  ]);
  const contract = useSelector(selectSelectedTrialContractInfo);

  const page = pageOf(pathname);
  // Same condition as the breeder report's own redirect (AnalyticsLayout): a
  // site it would send away is not worth the trip.
  const analytics =
    hasAnalyticsRole(contract?.roles) && traitsListForMap.length > 0;

  const steps = useMemo(
    () => (active ? buildTourSteps({ analytics }) : []),
    [active, analytics]
  );

  const current = steps[stepIndex];
  // A site loaded on the map. Read from the store, not the router's path,
  // which may not carry it (see pageOf).
  const siteOnMap = page === "map" && Boolean(trial) && !refreshing;

  // First site opened on the map. Rescheduled while the map's fetches land, so
  // the HUD has laid out before the spotlight measures it; `startedOnce` is only
  // set by the dispatch, so a cancelled timer delays the tour instead of losing
  // it.
  useEffect(() => {
    if (active || startedOnce || tourDone || !siteOnMap) return undefined;
    const timer = setTimeout(() => dispatch(startTour()), 800);
    return () => clearTimeout(timer);
  }, [active, startedOnce, tourDone, siteOnMap, dispatch]);

  // A step whose target the user may click instead of Next (the compass): that
  // click is the gesture the step teaches, so it moves the tour on too.
  // Capture phase, so it runs whatever the target does with the event.
  // The next step (the dial) waits for its own target through `open`.
  useEffect(() => {
    if (!current?.advanceOnClick || current.page !== page) return undefined;
    const target = document.querySelector(current.target);
    if (!target) return undefined;
    const onClick = () => dispatch(setTourStep(stepIndex + 1));
    target.addEventListener("click", onClick, true);
    return () => target.removeEventListener("click", onClick, true);
  }, [current, page, stepIndex, dispatch]);

  // A step that first opens a panel (`open`, see interfaceGuidedTour) is held
  // back until the panel is on screen: Joyride measures its target once, when
  // the step shows, so a panel still opening would get a wrong spotlight.
  const [openedStep, setOpenedStep] = useState(null);
  const opening = Boolean(current?.open) && openedStep !== stepIndex;
  useEffect(() => {
    if (!opening || current.page !== page) return undefined;
    let cancelled = false;
    current
      .open(() => cancelled)
      .then(() => !cancelled && setOpenedStep(stepIndex));
    return () => {
      cancelled = true;
    };
  }, [opening, current, page, stepIndex]);

  // A step whose target the user can close while it shows (`nextIfClosed`: the
  // dial closes on a click outside it, its cross or N). Once it is gone, the
  // overlay measures its spotlight around nothing and swallows every click.
  // Closing it is a deliberate "got it", so move on to the next step.
  useEffect(() => {
    if (!current?.nextIfClosed || opening || current.page !== page)
      return undefined;
    const target = document.querySelector(current.target);
    if (!target) return undefined;
    const observer = new MutationObserver(() => {
      if (target.isConnected) return;
      dispatch(setTourStep(stepIndex + 1));
    });
    observer.observe(target.parentNode, { childList: true });
    return () => observer.disconnect();
  }, [current, opening, page, stepIndex, dispatch]);

  // Safety net for a route change the tour did not ask for (the user opened
  // another page, or came back to the map): land on the step of the page they
  // are on rather than sit there invisible.
  useEffect(() => {
    if (!active) return;
    // No step at that index: the list shrank under us (the analytics step went)
    // or we ran past the end. Nothing can be shown from here, and an active
    // tour with nothing to show blocks it from starting again, so end it.
    if (!current) {
      dispatch(stopTour());
      return;
    }
    if (!page || current.page === page) return;
    const resumeAt = steps.findIndex(
      (step, index) => index > stepIndex && step.page === page
    );
    if (resumeAt !== -1) {
      dispatch(setTourStep(resumeAt));
      return;
    }
    // Back on a page the tour is already past: the user left the flow, so drop
    // the tour rather than keep it alive and invisible. The completion flag is
    // only set when the user finishes or skips, so it is offered again on the
    // next load of the app.
    dispatch(stopTour());
  }, [active, page, current, steps, stepIndex, dispatch]);

  const completeTour = () => {
    requestSetCompletedTour(user)
      .catch(() => {})
      .finally(() => dispatch(refreshUser()));
    dispatch(stopTour());
  };

  const handleCallback = ({ action, index, status, type }) => {
    // The two deliberate ways out: Skip, which Joyride reports as a status, and
    // Escape, which it reports as a close action on the current step. Both burn
    // the tour. A stray click on the dimmed page is not one of them: the close
    // button is hidden and the overlay does not close on click, so an accident
    // cannot consume a tour the user gets once.
    if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
      completeTour();
      return;
    }
    // Skip also emits STEP_AFTER (with its own action) just before the status
    // above lands, so only a real Next moves the counter.
    if (type !== EVENTS.STEP_AFTER || action !== ACTIONS.NEXT) return;
    // The back button is hidden, so this only ever runs forward.
    const next = index + 1;
    // Past the last step: burn the tour here. Joyride never does it for us,
    // since we drive `stepIndex`: in controlled mode its own index never moves,
    // so it never reaches the end of the list and STATUS.FINISHED never comes
    // (store.getNextState, dist/index.js).
    if (next >= steps.length) {
      completeTour();
      return;
    }
    dispatch(setTourStep(next));
    // Crossing to Analytics: the tour takes the user there itself, and resumes
    // on arrival since the next step already belongs to that page. The site
    // stays the one open on the map, Analytics reads it from the same store.
    if (steps[next].page === "analytics" && steps[index].page !== "analytics")
      history.push("/analytics/breeder");
  };

  // Paused, not stopped: the route is mid-change, the user wandered off, or the
  // step's panel is still opening. The tour picks up where it left off.
  if (!current || current.page !== page || opening) return null;

  return (
    <Joyride
      run
      stepIndex={stepIndex}
      steps={steps}
      continuous
      showProgress
      showSkipButton
      hideBackButton
      hideCloseButton
      // A click on the dimmed page is an accident, not an answer: without this,
      // Joyride closes the tour on it, and since the tour is shown once, it was
      // gone for good (helpers.close("overlay"), dist/index.js:1924).
      disableOverlayClose
      spotlightClicks
      // Analytics scrolls inside `.page-content`, not the document. Without
      // this, Joyride "fixes" that container by forcing `overflow: initial` on
      // it and places its spotlight against the document instead of the real
      // scroll parent.
      disableScrollParentFix
      // Every target is already on screen (the map HUD, a fixed drawer).
      // Scrolling only made room for a tooltip near the bottom edge, and it
      // scrolled the whole document: the app slid up and cut the navbar.
      disableScrolling
      callback={handleCallback}
      styles={{
        tooltip: { padding: "8px" },
        options: { zIndex: 10000, primaryColor: HIPHEN_GREEN },
      }}
      locale={{ last: "Finish", next: "Next", skip: "Skip" }}
    />
  );
};
